import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getDatabase, onValue, ref } from 'firebase/database';
import { useAuth } from './AuthContext';
import { useUsersContext } from './UsersContext';
import { hasRole, CAN_VIEW_ORG_CALENDAR } from '../config/roles';

const CalendarContext = createContext();

// A punch as stored, or '' when there is none. Timecard edits write the
// placeholder 'Missing' for an empty punch, which is not a time.
const punchValue = (v) => {
  const s = String(v ?? '').trim();
  return s && s.toLowerCase() !== 'missing' ? s : '';
};

export const useCalendarContext = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendarContext must be used within a CalendarProvider');
  }
  return context;
};

export const CalendarProvider = ({ children }) => {
  const { user, role } = useAuth();
  const { findUserById } = useUsersContext() || {};
  // Manual events (UI-managed)
  const [events, setEvents] = useState([]);
  // Attendance-driven events (read-only)
  const [attendanceEvents, setAttendanceEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Scope for subscription source: 'auto' (by role), 'org' (attendanceByDate), 'self' (attendanceByUser)
  const [scope, setScope] = useState('auto');
  // Visible month selection (YYYY-MM), default to current month
  const now = new Date();
  const [selectedYearMonth, setSelectedYearMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );

  // Event types with colors for HR management (light and dark mode compatible)
  const eventTypes = {
    meeting: {
      color: '#3B82F6',
      bgColor: '#DBEAFE',
      badgeColor: 'info',
      darkColor: '#60A5FA',
      darkBgColor: 'rgba(59, 130, 246, 0.1)'
    },
    training: {
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      badgeColor: 'warning',
      darkColor: '#FBBF24',
      darkBgColor: 'rgba(245, 158, 11, 0.1)'
    },
    holiday: {
      color: '#EF4444',
      bgColor: '#FEE2E2',
      badgeColor: 'failure',
      darkColor: '#F87171',
      darkBgColor: 'rgba(239, 68, 68, 0.1)'
    },
    task: {
      color: '#64748B',
      bgColor: '#E2E8F0',
      badgeColor: 'gray',
      darkColor: '#9CA3AF',
      darkBgColor: 'rgba(100, 116, 139, 0.1)'
    },
    attendance: {
      color: '#10B981',
      bgColor: '#D1FAE5',
      badgeColor: 'success',
      darkColor: '#34D399',
      darkBgColor: 'rgba(16, 185, 129, 0.1)'
    },
    leave: {
      color: '#8B5CF6',
      bgColor: '#EDE9FE',
      badgeColor: 'purple',
      darkColor: '#A78BFA',
      darkBgColor: 'rgba(139, 92, 246, 0.1)'
    }
  };

  // Initialize with empty events (clean frontend)
  useEffect(() => {
    setEvents([]);
  }, []);

  // Subscribe to attendance: HR/Admin -> org-wide by date; Employee -> own by user
  useEffect(() => {
    if (!selectedYearMonth) {
      setAttendanceEvents([]);
      return;
    }

    const db = getDatabase();
    const workHours = { AM: { start: '08:00', end: '12:00' }, PM: { start: '13:00', end: '17:00' } };
    const [yyyy, mm] = selectedYearMonth.split('-');

    // Helper to build start/end based on halfDay
    const buildTimes = (dateStr, halfDay) => {
      const mkDateTime = (date, time) => new Date(`${date}T${time}:00`);
      if (halfDay === 'AM') {
        return {
          start: mkDateTime(dateStr, workHours.AM.start),
          end: mkDateTime(dateStr, workHours.AM.end),
          allDay: false,
        };
      }
      if (halfDay === 'PM') {
        return {
          start: mkDateTime(dateStr, workHours.PM.start),
          end: mkDateTime(dateStr, workHours.PM.end),
          allDay: false,
        };
      }
      return {
        start: new Date(`${dateStr}T00:00:00`),
        end: new Date(`${dateStr}T23:59:59`),
        allDay: true,
      };
    };

    const isPrivRole = hasRole(role, CAN_VIEW_ORG_CALENDAR);
    const useOrg = scope === 'org' || (scope === 'auto' && isPrivRole);

    let unsubscribe = () => {};

    if (useOrg) {
      // HR/Admin: subscribe org-wide by date for the selected month
      const monthRef = ref(db, `attendanceByDate/${selectedYearMonth}`);
      unsubscribe = onValue(monthRef, (snap) => {
        const val = snap.val() || {};
        const mapped = [];

        Object.entries(val).forEach(([dd, usersForDay]) => {
          if (!usersForDay || typeof usersForDay !== 'object') return;
          const day = String(dd).padStart(2, '0');
          const dateStr = `${yyyy}-${mm}-${day}`;

          const presentUsers = [];
          const absentUsers = [];
          const leaveUsers = [];
          const otByUid = new Map();

          Object.entries(usersForDay).forEach(([uid, rec]) => {
            if (!rec) return;

            const status = String(rec.status || '').toLowerCase();
            const info = findUserById ? findUserById(uid) : null;
            const storedFirst = rec.firstName || (rec.fullName ? String(rec.fullName).split(' ')[0] : (rec.name ? String(rec.name).split(' ')[0] : '')) || '';
            const storedPhoto = rec.userPhoto || rec.photoURL || '';

            const name = storedFirst || info?.firstName || info?.fullName || info?.name || info?.email || uid;
            const firstName = storedFirst || info?.firstName || (info?.fullName ? String(info.fullName).split(' ')[0] : (info?.name ? String(info.name).split(' ')[0] : '')) || uid;
            const userPhoto = storedPhoto || info?.userPhoto || info?.photoURL || '';

            const hasPunchData = !!(punchValue(rec.timeIn) || punchValue(rec.timeOut) || punchValue(rec.firstIn) || punchValue(rec.lastOut));
            const hasLeave = status.includes('leave');
            const hasOvertime = status.includes('overtime') || status === 'ot';
            const isAbsent = status.includes('absent') || (!hasPunchData && !hasLeave && !hasOvertime);
            const isPresent = !hasLeave && !hasOvertime && (hasPunchData || status === 'present');

            if (isPresent) {
              presentUsers.push({ uid, name, firstName, userPhoto });
            } else if (isAbsent) {
              absentUsers.push({ uid, name, firstName, userPhoto });
            }

            if (hasLeave) {
              leaveUsers.push({
                uid,
                name,
                firstName,
                userPhoto,
                halfDay: rec.halfDay || 'FULL',
                typeOfLeave: rec.typeOfLeave || '',
                withPay: !!rec.withPay,
                requestId: rec.requestId || null,
              });
            }

            if (hasOvertime) {
              otByUid.set(uid, {
                uid,
                name,
                firstName,
                userPhoto,
                isOvertime: true,
                otStart: rec.startTime || rec.otStart || rec.timeFrom || '',
                otEnd: rec.endTime || rec.otEnd || rec.timeTo || '',
                otHours: rec.hours || rec.otHours || rec.overtimeHours || '',
                requestId: rec.requestId || null,
              });
            }

            if (rec.overtime && typeof rec.overtime === 'object') {
              const ot = rec.overtime;
              otByUid.set(uid, {
                uid,
                name,
                firstName,
                userPhoto,
                isOvertime: true,
                otStart: ot.startTime || ot.otStart || ot.timeFrom || '',
                otEnd: ot.endTime || ot.otEnd || ot.timeTo || '',
                otHours: ot.hours || ot.otHours || ot.overtimeHours || '',
                requestId: ot.requestId || null,
              });
            }
          });

          const overtimeUsers = Array.from(otByUid.values());
          const hasAbsence = absentUsers.length > 0;

          if (hasAbsence) {
            mapped.push({
              id: `attendance-summary-${dateStr}`,
              title: `${absentUsers.length} Absent`,
              start: new Date(`${dateStr}T00:00:00`).toISOString(),
              end: new Date(`${dateStr}T23:59:59`).toISOString(),
              allDay: true,
              extendedProps: {
                isAttendanceEvent: true,
                type: 'attendance-summary',
                absentUsers,
                leaveUsers,
                overtimeUsers,
              },
            });
          } else {
            if (leaveUsers.length) {
              mapped.push({
                id: `leave-agg-${dateStr}`,
                title: leaveUsers.map(u => u.firstName || u.name).join(', '),
                start: new Date(`${dateStr}T00:00:00`).toISOString(),
                end: new Date(`${dateStr}T23:59:59`).toISOString(),
                allDay: true,
                extendedProps: { isAttendanceEvent: true, type: 'leave', users: leaveUsers },
              });
            }

            if (overtimeUsers.length) {
              mapped.push({
                id: `ot-agg-${dateStr}`,
                title: overtimeUsers.map(u => u.firstName || u.name).join(', '),
                start: new Date(`${dateStr}T00:00:00`).toISOString(),
                end: new Date(`${dateStr}T23:59:59`).toISOString(),
                allDay: true,
                extendedProps: { isAttendanceEvent: true, type: 'overtime', users: overtimeUsers },
              });
            }
          }
        });

        setAttendanceEvents(mapped);
        setError(null);
      }, (err) => {
        console.error('attendanceByDate subscription error', err);
        setAttendanceEvents([]);
        setError(err.message);
      });
    } else {
      if (!user?.uid) {
        setAttendanceEvents([]);
        return;
      }
      // Employee/self: subscribe to own month by day
      const userMonthRef = ref(db, `attendanceByUser/${user.uid}/${selectedYearMonth}`);
      const selfInfo = findUserById ? findUserById(user.uid) : null;
      unsubscribe = onValue(userMonthRef, (snap) => {
        const val = snap.val() || {};
        const mapped = [];

        Object.entries(val).forEach(([dd, rec]) => {
          if (!rec) return;
          const dateStr = `${yyyy}-${mm}-${String(dd).padStart(2, '0')}`;
          const status = String(rec.status || '').toLowerCase();

          const storedFirst = rec.firstName || (rec.fullName ? String(rec.fullName).split(' ')[0] : (rec.name ? String(rec.name).split(' ')[0] : '')) || '';
          const storedPhoto = rec.userPhoto || rec.photoURL || '';
          const titleName = storedFirst || selfInfo?.firstName || selfInfo?.name || '';
          const userPhoto = storedPhoto || selfInfo?.userPhoto || selfInfo?.photoURL || '';

          // Timecards also mark a day LWOP / LWP (leave without / with pay).
          const hasLeave = status.includes('leave') || status === 'lwop' || status === 'lwp';
          const timeIn = punchValue(rec.timeIn) || punchValue(rec.firstIn);
          const timeOut = punchValue(rec.timeOut) || punchValue(rec.lastOut);
          const hasPunchData = !!(timeIn || timeOut);

          // A day can carry both a leave and punches (a half-day leave, or
          // punches kept when a leave was approved later), so show both.
          if (hasPunchData) {
            mapped.push({
              id: `attendance-${user.uid}-${dateStr}`,
              title: titleName || 'Attendance',
              start: new Date(`${dateStr}T00:00:00`).toISOString(),
              end: new Date(`${dateStr}T23:59:59`).toISOString(),
              allDay: true,
              extendedProps: {
                isAttendanceEvent: true,
                type: 'attendance',
                uid: user.uid,
                timeIn,
                timeOut,
                status: hasLeave ? 'Present' : (rec.status || 'Present'),
                userPhoto,
                firstName: storedFirst || selfInfo?.firstName || '',
              },
            });
          }
          if (hasLeave) {
            const halfDay = rec.halfDay || 'FULL';
            const times = buildTimes(dateStr, halfDay);
            mapped.push({
              id: `leave-${user.uid}-${dateStr}`,
              title: titleName || 'On Leave',
              start: times.start.toISOString(),
              end: times.end.toISOString(),
              allDay: times.allDay,
              extendedProps: {
                isAttendanceEvent: true,
                type: 'leave',
                uid: user.uid,
                halfDay,
                typeOfLeave: rec.typeOfLeave || (status === 'lwop' ? 'Leave without pay' : status === 'lwp' ? 'Leave with pay' : ''),
                withPay: status === 'lwp' || !!rec.withPay,
                requestId: rec.requestId || null,
                userPhoto,
                firstName: storedFirst || selfInfo?.firstName || '',
              },
            });
          }

          // Determine overtime source: prefer nested overtime over root status
          let otSrc = null;
          if (rec.overtime && typeof rec.overtime === 'object') {
            otSrc = rec.overtime;
          } else if (status.includes('overtime') || status === 'ot') {
            otSrc = rec;
          }

          if (otSrc) {
            const startStr = otSrc.startTime || otSrc.otStart || otSrc.timeFrom || '17:00';
            const endStr = otSrc.endTime || otSrc.otEnd || otSrc.timeTo || '20:00';
            const start = new Date(`${dateStr}T${startStr}:00`);
            const end = new Date(`${dateStr}T${endStr}:00`);
            mapped.push({
              id: `ot-${user.uid}-${dateStr}`,
              title: titleName || 'Overtime',
              start: start.toISOString(),
              end: end.toISOString(),
              allDay: false,
              extendedProps: {
                isAttendanceEvent: true,
                type: 'overtime',
                uid: user.uid,
                otStart: startStr,
                otEnd: endStr,
                otHours: otSrc.hours || otSrc.otHours || otSrc.overtimeHours || '',
                userPhoto,
                firstName: storedFirst || selfInfo?.firstName || '',
              },
            });
          }
        });

        setAttendanceEvents(mapped);
        setError(null);
      }, (err) => {
        console.error('attendanceByUser subscription error', err);
        setAttendanceEvents([]);
        setError(err.message);
      });
    }

    return () => {
      try { unsubscribe(); } catch {}
    };
  }, [user?.uid, role, selectedYearMonth, findUserById, scope]);

  // Combined events
  const combinedEvents = useMemo(() => {
    return [...events, ...attendanceEvents];
  }, [events, attendanceEvents]);

  // Add new event (clean frontend)
  const addEvent = async (eventData) => {
    try {
      setLoading(true);
      
      const newEvent = {
        id: Date.now().toString(),
        ...eventData,
        extendedProps: {
          ...eventData.extendedProps,
          type: eventData.type || 'meeting'
        }
      };
      
      setEvents(prevEvents => [...prevEvents, newEvent]);
      setLoading(false);
      
      return newEvent;
    } catch (err) {
      console.error('Error adding event:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Update existing event (clean frontend)
  const updateEvent = async (eventId, eventData) => {
    try {
      setLoading(true);
      
      const updatedEvent = {
        ...eventData,
        id: eventId,
        extendedProps: {
          ...eventData.extendedProps,
          type: eventData.type || 'meeting'
        }
      };
      
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === eventId ? updatedEvent : event
        )
      );
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Delete event (clean frontend)
  const deleteEvent = async (eventId) => {
    try {
      setLoading(true);
      
      setEvents(prevEvents => 
        prevEvents.filter(event => event.id !== eventId)
      );
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Get events by type
  const getEventsByType = (type) => {
    return combinedEvents.filter(event => event.extendedProps?.type === type);
  };

  // Get events for specific date range
  const getEventsInRange = (start, end) => {
    return combinedEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end || event.start);
      return eventStart >= start && eventEnd <= end;
    });
  };


  const value = {
    events: combinedEvents,
    setEvents,
    selectedYearMonth,
    setSelectedYearMonth,
    setScope,
    addEvent,
    updateEvent,
    deleteEvent,
    getEventsByType,
    getEventsInRange,
    eventTypes,
    loading,
    error
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};
